import React, {useEffect, useMemo} from "react";
import {IAdvancedState} from "../../reducers/createNewProjectReducer.ts";
import Switch from "../Switch.tsx";
import {titleCase} from "../../utils/utils.ts";
import Field from "../Field.tsx";
import {FcDataConfiguration} from "react-icons/fc";
import useSystemStore from "../../store/useSystemStore.ts";
import iso6391 from "iso-639-1";

interface AdvancedStepProps {
  advancedState: IAdvancedState;
  toggleAudioDescription: (value: boolean) => void;
  setAudioDescriptionLanguages: (languages: string[]) => void;
  configurationName: string;
  setConfigurationName: (name: string) => void;
}

const AdvancedStep: React.FC<AdvancedStepProps> = (
  {
    advancedState,
    toggleAudioDescription,
    setAudioDescriptionLanguages,
    configurationName,
    setConfigurationName
  }: AdvancedStepProps) => {
  const {supportedCodeLanguages} = useSystemStore()

  const supportedSourceLanguagesNames: string[] = useMemo(() => {
    return supportedCodeLanguages.map((langCode: string) => iso6391.getName(langCode));
  }, [supportedCodeLanguages]);

  useEffect(() => {
    const isSelected: boolean = Boolean(advancedState?.isSelected)
    if (!isSelected) {
      const timeout = setTimeout(() => {
        setAudioDescriptionLanguages([]);
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [advancedState?.isSelected, setAudioDescriptionLanguages]);


  const handleAdLanguage = (language: string) => {
    const currentVisualTags: string[] = advancedState?.audioDescription || [];
    if (currentVisualTags.includes(language)) {
      setAudioDescriptionLanguages(currentVisualTags.filter(tag => tag !== language));
    } else {
      setAudioDescriptionLanguages([...currentVisualTags, language]);
    }
  }

  return (
    <div className="flex flex-col w-full">
      {/* Metadata step content goes here */}
      <div
        className={`w-full p-6 rounded-md border-brandPurple-500 ${advancedState?.isSelected ? " border-2" : " border"}`}
      >
        <div className="flex items-center justify-between mb-2 ">
          <p className="text-xl font-semibold text-white">
            Audio Description
          </p>
          <Switch
            value={advancedState?.isSelected ?? false}
            setValue={toggleAudioDescription}/>
        </div>
        <p className="text-xs text-n-400">
          Generate precisely-timed narration that describes important visual elements during natural pauses in dialogue,
          making your content accessible to viewers with visual impairments.
        </p>
        {/*  collapse using react and tailwind*/}
        <div
          className={`transition-all ${advancedState?.isSelected ? "duration-1000" : "duration-500"} ease-in-out overflow-hidden ${
            advancedState?.isSelected ? "max-h-[400px]" : "max-h-0"
          }`}
        >
          <div className="mt-4 px-1 pb-2">
            <div className="flex flex-col">
              <div className="grid grid-cols-5 gap-6">
                {Object.values(supportedSourceLanguagesNames).map((language: string) => {
                  const isSelected: boolean = Boolean(advancedState?.audioDescription?.includes(language))
                  return (
                    <button
                      key={language}
                      onClick={() => handleAdLanguage(language)}
                      className={`min-w-[100px] min-h-[40px] px-2 py-1 rounded-full text-sm border ${isSelected ? 'bg-brandPurple-500 border-transparent text-white' : 'border-n-400 text-n-400 hover:border-brandPurple-500 hover:text-n-200'}  hover:scale-105 transition-transform `}
                    >
                      {titleCase(language)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Field
        className="mt-8"
        label="Save Configuration [Optional]"
        placeholder="Enter configuration name for future use"
        icon={FcDataConfiguration}
        value={configurationName}
        onChange={(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => setConfigurationName(event.target.value)}
      />
    </div>
  );
};

export default AdvancedStep;